import { useEffect, useState } from 'react';
import {
  collection, onSnapshot, updateDoc, doc,
  query, where, getDocs, addDoc, serverTimestamp
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { Link } from 'react-router-dom';

export default function Users() {
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState('');
  const [selectedUser, setSelectedUser] = useState(null);
  const [userBookings, setUserBookings] = useState([]);
  const [walletHistory, setWalletHistory] = useState([]);
  const [activeTab, setActiveTab] = useState('profile');
  const [amount, setAmount] = useState('');
  const [note, setNote] = useState('');
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const unsub = onSnapshot(collection(db, 'users'), (snap) => {
      const data = snap.docs.map(d => ({ id: d.id, ...d.data() }));
      setUsers(data);
    });
    return () => unsub();
  }, []);

  const openUser = async (user) => {
    setSelectedUser(user);
    setActiveTab('profile');
    setAmount('');
    setNote('');

    // Load bookings
    const bSnap = await getDocs(
      query(collection(db, 'bookings'), where('userId', '==', user.id))
    );
    const bookings = bSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    bookings.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
    setUserBookings(bookings);

    // Load wallet history
    const wSnap = await getDocs(
      query(collection(db, 'walletHistory'), where('userId', '==', user.id))
    );
    const history = wSnap.docs.map(d => ({ id: d.id, ...d.data() }));
    history.sort((a, b) => (b.createdAt?.seconds ?? 0) - (a.createdAt?.seconds ?? 0));
    setWalletHistory(history);
  };

  const handleAddBalance = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      alert('সঠিক amount দাও!');
      return;
    }
    setLoading(true);
    try {
      const newBalance = (selectedUser.balance ?? 0) + Number(amount);
      await updateDoc(doc(db, 'users', selectedUser.id), { balance: newBalance });

      // Save wallet history
      await addDoc(collection(db, 'walletHistory'), {
        userId: selectedUser.id,
        type: 'credit',
        amount: Number(amount),
        note: note || 'Admin added balance',
        balanceAfter: newBalance,
        createdAt: serverTimestamp(),
      });

      // Refresh
      setSelectedUser({ ...selectedUser, balance: newBalance });
      setWalletHistory(prev => [{
        type: 'credit',
        amount: Number(amount),
        note: note || 'Admin added balance',
        balanceAfter: newBalance,
        createdAt: { seconds: Date.now() / 1000 },
      }, ...prev]);

      setAmount('');
      setNote('');
      alert(`✅ ৳${amount} add হয়েছে!`);
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(false);
  };

  const handleAddPrize = async () => {
    if (!amount || isNaN(amount) || Number(amount) <= 0) {
      alert('সঠিক amount দাও!');
      return;
    }
    setLoading(true);
    try {
      const newBalance = (selectedUser.balance ?? 0) + Number(amount);
      await updateDoc(doc(db, 'users', selectedUser.id), { balance: newBalance });
      await addDoc(collection(db, 'walletHistory'), {
        userId: selectedUser.id,
        type: 'prize',
        amount: Number(amount),
        note: note || 'Tournament prize',
        balanceAfter: newBalance,
        createdAt: serverTimestamp(),
      });
      setSelectedUser({ ...selectedUser, balance: newBalance });
      setWalletHistory(prev => [{
        type: 'prize',
        amount: Number(amount),
        note: note || 'Tournament prize',
        balanceAfter: newBalance,
        createdAt: { seconds: Date.now() / 1000 },
      }, ...prev]);
      setAmount('');
      setNote('');
      alert(`✅ Prize ৳${amount} add হয়েছে!`);
    } catch (e) {
      alert('Error: ' + e.message);
    }
    setLoading(false);
  };

  const filtered = users.filter(u =>
  (u.username ?? '').toLowerCase().includes(search.toLowerCase()) ||
  (u.phone ?? '').toString().includes(search) ||
  (u.id ?? '').toString().toLowerCase().includes(search.toLowerCase())
);

  const sidebarLinks = [
    { to: '/', label: '🏠 Dashboard' },
    { to: '/matches', label: '🎯 Matches' },
    { to: '/results', label: '🏆 Results' },
    { to: '/users', label: '👥 Users', active: true },
    { to: '/deposits', label: '💰 Deposits' },
    { to: '/withdrawals', label: '⬆️ Withdrawals' },
    { to: '/settings', label: '⚙️ Settings' },
    { to: '/password-resets', label: '🔑 Password Resets' },
    { to: '/videos', label: '🎬 Videos' }
  ];

  return (
    <div style={styles.container}>

      {/* Sidebar */}
      <div style={styles.sidebar}>
        <div style={styles.sidebarLogo}>🎮 FF Arena</div>
        <div style={styles.sidebarSub}>Admin Panel</div>
        <nav style={styles.nav}>
          {sidebarLinks.map(l => (
            <Link key={l.to} to={l.to}
              style={l.active ? styles.navItemActive : styles.navItem}>
              {l.label}
            </Link>
          ))}
        </nav>
      </div>

      {/* Main */}
      <div style={styles.main}>
        <div style={styles.topRow}>
          <div>
            <h1 style={styles.pageTitle}>👥 Users</h1>
            <p style={styles.pageSub}>সব user দেখো এবং wallet manage করো</p>
          </div>
          <input
            style={styles.searchInput}
            placeholder="🔍 Username বা phone দিয়ে খোঁজো"
            value={search}
            onChange={e => setSearch(e.target.value)}
          />
        </div>

        {/* Stats */}
        <div style={styles.statsRow}>
          <div style={styles.statCard}>
            <div style={styles.statValue}>{users.length}</div>
            <div style={styles.statLabel}>Total Users</div>
          </div>
          <div style={styles.statCard}>
            <div style={{ color: '#00c896', fontSize: 28, fontWeight: 'bold', marginBottom: 4 }}>
              ৳{users.reduce((s, u) => s + (u.balance ?? 0), 0)}
            </div>
            <div style={styles.statLabel}>Total Wallet Balance</div>
          </div>
        </div>

        {/* User List */}
        <div style={styles.tableCard}>
          {/* Table Header */}
          <div style={styles.tableHeader}>
            <span style={{ flex: 2 }}>User</span>
            <span style={{ flex: 1 }}>Phone</span>
            <span style={{ flex: 1 }}>Balance</span>
            <span style={{ flex: 1 }}>Joined</span>
            <span style={{ flex: 1 }}>Action</span>
          </div>

          {filtered.length === 0 ? (
            <div style={styles.emptyRow}>কোনো user নেই</div>
          ) : filtered.map((u, i) => (
            <div key={u.id} style={styles.tableRow}>
              <div style={{ flex: 2, display: 'flex', alignItems: 'center', gap: 12 }}>
                <div style={styles.avatar}>
                  {(u.username ?? u.phone ?? '?')[0].toUpperCase()}
                </div>
                <div>
                  <div style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>
                    {u.username ?? '(no username)'}
                  </div>
                  <div style={{ color: '#555', fontSize: 11 }}>
                    UID: {u.id.slice(0, 10)}...
                  </div>
                </div>
              </div>
              <div style={{ flex: 1, color: '#aaa', fontSize: 13 }}>{u.phone ?? '—'}</div>
              <div style={{ flex: 1 }}>
                <span style={{ color: '#00c896', fontWeight: 'bold' }}>৳{u.balance ?? 0}</span>
              </div>
              <div style={{ flex: 1, color: '#666', fontSize: 12 }}>
                {u.createdAt?.seconds
                  ? new Date(u.createdAt.seconds * 1000).toLocaleDateString()
                  : '—'}
              </div>
              <div style={{ flex: 1 }}>
                <button style={styles.detailsBtn} onClick={() => openUser(u)}>
                  👁 Details
                </button>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* User Detail Modal */}
      {selectedUser && (
        <div style={styles.modalOverlay}>
          <div style={styles.detailModal}>

            {/* Modal Header */}
            <div style={styles.modalHeader}>
              <div style={styles.modalAvatar}>
                {(selectedUser.username ?? selectedUser.phone ?? '?')[0].toUpperCase()}
              </div>
              <div style={{ flex: 1 }}>
                <div style={styles.modalName}>{selectedUser.username ?? '(no username)'}</div>
                <div style={styles.modalPhone}>{selectedUser.phone ?? '—'}</div>
              </div>
              <div style={styles.modalBalance}>৳{selectedUser.balance ?? 0}</div>
              <button style={styles.closeBtn} onClick={() => setSelectedUser(null)}>✕</button>
            </div>

            {/* Tabs */}
            <div style={styles.tabBar}>
              {[
                { key: 'profile', label: '👤 Profile' },
                { key: 'wallet', label: '💰 Wallet' },
                { key: 'slots', label: '🎟️ Slots' },
              ].map(t => (
                <button key={t.key}
                  style={activeTab === t.key ? styles.tabActive : styles.tab}
                  onClick={() => setActiveTab(t.key)}>
                  {t.label}
                </button>
              ))}
            </div>

            {/* Tab Content */}
            <div style={styles.tabContent}>

              {/* Profile Tab */}
              {activeTab === 'profile' && (
                <div style={styles.profileGrid}>
                 {[
                     { label: '👤 Full Name', value: selectedUser.name ?? '—' },
                     { label: '🔖 Username', value: selectedUser.username ?? '—' },
                     { label: '📱 Phone', value: selectedUser.phone ?? '—' },
                     { label: '📧 Email', value: selectedUser.email ?? '—' },
                     { label: '💰 Balance', value: `৳${selectedUser.balance ?? 0}` },
                     { label: '🎟️ Total Slots', value: userBookings.length },
                     { label: '🆔 User ID', value: selectedUser.id },
                {
              label: '📅 Joined',
              value: selectedUser.createdAt?.seconds
              ? new Date(selectedUser.createdAt.seconds * 1000).toLocaleDateString()
              : '—'
              },
              ].map((item, i) => (
                    <div key={i} style={styles.profileItem}>
                      <div style={styles.profileLabel}>{item.label}</div>
                      <div style={styles.profileValue}>{item.value}</div>
                    </div>
                  ))}
                </div>
              )}

              {/* Wallet Tab */}
              {activeTab === 'wallet' && (
                <div>
                  {/* Add Balance Form */}
                  
                    <div style={{ display: 'flex', gap: 10, marginBottom: 10 }}>
  <input
    style={{ ...styles.input, flex: 1 }}
    type="number"
    placeholder="Amount (৳)"
    value={amount}
    onChange={e => setAmount(e.target.value)}
  />
  <input
    style={{ ...styles.input, flex: 2 }}
    placeholder="Note (যেমন: 1st Place prize)"
    value={note}
    onChange={e => setNote(e.target.value)}
  />
  <button
    style={{ ...styles.addBtn, opacity: loading ? 0.6 : 1 }}
    onClick={handleAddBalance}
    disabled={loading}
  >
    {loading ? '...' : '➕ Balance'}
  </button>
  <button
    style={{ ...styles.addBtn, background: '#16a34a', opacity: loading ? 0.6 : 1 }}
    onClick={handleAddPrize}
    disabled={loading}
  >
    {loading ? '...' : '🏆 Prize'}
  </button>
</div>



                  {/* Wallet History */}
                  <div style={styles.historyTitle}>📋 Wallet History</div>
                  {walletHistory.length === 0 ? (
                    <div style={styles.emptySmall}>কোনো history নেই</div>
                  ) : walletHistory.map((h, i) => (
                    <div key={i} style={styles.historyRow}>
                      <div style={{
                        width: 36, height: 36, borderRadius: 18,
                        background: h.type === 'credit' ? '#16a34a22' : '#ff444422',
                        display: 'flex', alignItems: 'center', justifyContent: 'center',
                        fontSize: 16,
                      }}>
                        {h.type === 'credit' ? '⬆️' : '⬇️'}
                      </div>
                      <div style={{ flex: 1 }}>
                        <div style={{ color: '#fff', fontSize: 13, fontWeight: '500' }}>
                          {h.note}
                        </div>
                        <div style={{ color: '#666', fontSize: 11 }}>
                          {h.createdAt?.seconds
                            ? new Date(h.createdAt.seconds * 1000).toLocaleString()
                            : 'Just now'}
                        </div>
                      </div>
                      <div>
                        <div style={{
                          color: h.type === 'credit' ? '#00c896' : '#ff4444',
                          fontWeight: 'bold', fontSize: 15,
                        }}>
                          {h.type === 'credit' ? '+' : '-'}৳{h.amount}
                        </div>
                        <div style={{ color: '#555', fontSize: 11, textAlign: 'right' }}>
                          bal: ৳{h.balanceAfter}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

              {/* Slots Tab */}
              {activeTab === 'slots' && (
                <div>
                  {userBookings.length === 0 ? (
                    <div style={styles.emptySmall}>কোনো slot নেই</div>
                  ) : userBookings.map((b, i) => (
                    <div key={i} style={styles.slotRow}>
                      <div style={styles.slotLeft}>
                        <div style={{ color: '#fff', fontWeight: '600', fontSize: 14 }}>
                          {b.matchTitle}
                        </div>
                        <div style={{ color: '#888', fontSize: 12, marginTop: 3 }}>
                          🎮 {b.gameId} &nbsp;·&nbsp; 👥 {b.joinType} &nbsp;·&nbsp; 🕐 {b.matchTime}
                        </div>
                      </div>
                      <div style={{ textAlign: 'right' }}>
                        <div style={{ color: '#ff6b35', fontWeight: 'bold' }}>
                          ৳{b.entryFee}
                        </div>
                        <div style={{
                          fontSize: 11, color: '#888', marginTop: 3,
                          background: '#1e1e3a', borderRadius: 6,
                          padding: '2px 8px', display: 'inline-block',
                        }}>
                          {b.matchMode}
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              )}

            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const styles = {
  container: { display: 'flex', minHeight: '100vh', background: '#0a0a1a' },
  sidebar: {
    width: 240, background: '#111128', borderRight: '1px solid #1e1e3a',
    padding: '24px 0', display: 'flex', flexDirection: 'column',
    position: 'fixed', height: '100vh',
  },
  sidebarLogo: { color: '#f4c430', fontSize: 20, fontWeight: 'bold', padding: '0 24px', marginBottom: 4 },
  sidebarSub: { color: '#555', fontSize: 12, padding: '0 24px', marginBottom: 32 },
  nav: { display: 'flex', flexDirection: 'column', flex: 1 },
  navItem: { color: '#888', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '500', borderLeft: '3px solid transparent' },
  navItemActive: { color: '#f4c430', textDecoration: 'none', padding: '12px 24px', fontSize: 14, fontWeight: '600', borderLeft: '3px solid #f4c430', background: '#f4c43011' },
  main: { marginLeft: 240, flex: 1, padding: 32 },
  topRow: { display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 24, gap: 16 },
  pageTitle: { color: '#fff', fontSize: 28, fontWeight: 'bold', margin: 0 },
  pageSub: { color: '#666', fontSize: 14, marginTop: 6 },
  searchInput: {
    background: '#111128', border: '1px solid #333', borderRadius: 10,
    color: '#fff', fontSize: 14, padding: '12px 16px', outline: 'none', width: 300,
  },
  statsRow: { display: 'flex', gap: 16, marginBottom: 24 },
  statCard: {
    background: '#111128', borderRadius: 14, border: '1px solid #1e1e3a',
    padding: '20px 28px', minWidth: 180,
  },
  statValue: { color: '#fff', fontSize: 28, fontWeight: 'bold', marginBottom: 4 },
  statLabel: { color: '#666', fontSize: 13 },
  tableCard: { background: '#111128', borderRadius: 16, border: '1px solid #1e1e3a', overflow: 'hidden' },
  tableHeader: {
    display: 'flex', alignItems: 'center', padding: '12px 20px',
    borderBottom: '1px solid #1e1e3a',
    color: '#555', fontSize: 12, fontWeight: '600', letterSpacing: 0.5,
  },
  tableRow: {
    display: 'flex', alignItems: 'center', padding: '14px 20px',
    borderBottom: '1px solid #1e1e3a11',
    transition: 'background 0.15s',
  },
  emptyRow: { textAlign: 'center', padding: 40, color: '#555' },
  avatar: {
    width: 40, height: 40, borderRadius: 20,
    background: '#f4c43022', border: '1px solid #f4c43044',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#f4c430', fontWeight: 'bold', fontSize: 16, flexShrink: 0,
  },
  detailsBtn: {
    background: '#f4c43022', border: '1px solid #f4c43044',
    color: '#f4c430', borderRadius: 8, padding: '6px 14px',
    cursor: 'pointer', fontSize: 13, fontWeight: '600',
  },

  // Modal
  modalOverlay: {
    position: 'fixed', top: 0, left: 0, right: 0, bottom: 0,
    background: '#000000bb', display: 'flex',
    alignItems: 'center', justifyContent: 'center', zIndex: 100,
  },
  detailModal: {
    background: '#111128', borderRadius: 20, border: '1px solid #1e1e3a',
    width: 680, maxHeight: '85vh', display: 'flex', flexDirection: 'column',
    overflow: 'hidden',
  },
  modalHeader: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '20px 24px', borderBottom: '1px solid #1e1e3a',
    background: '#0d0d20',
  },
  modalAvatar: {
    width: 52, height: 52, borderRadius: 26,
    background: '#f4c43022', border: '2px solid #f4c43066',
    display: 'flex', alignItems: 'center', justifyContent: 'center',
    color: '#f4c430', fontWeight: 'bold', fontSize: 22,
  },
  modalName: { color: '#fff', fontSize: 18, fontWeight: 'bold' },
  modalPhone: { color: '#888', fontSize: 13, marginTop: 2 },
  modalBalance: { color: '#00c896', fontSize: 22, fontWeight: 'bold', marginRight: 12 },
  closeBtn: {
    background: '#ff444422', border: '1px solid #ff444444',
    color: '#ff6b6b', borderRadius: 8, padding: '6px 12px',
    cursor: 'pointer', fontSize: 16,
  },
  tabBar: {
    display: 'flex', borderBottom: '1px solid #1e1e3a',
    background: '#0d0d20',
  },
  tab: {
    flex: 1, background: 'none', border: 'none',
    color: '#666', fontSize: 13, fontWeight: '600',
    padding: '14px', cursor: 'pointer', borderBottom: '2px solid transparent',
  },
  tabActive: {
  flex: 1, border: 'none',
  color: '#f4c430', fontSize: 13, fontWeight: '600',
  padding: '14px', cursor: 'pointer',
  borderBottom: '2px solid #f4c430',
  background: '#f4c43011',
},
  tabContent: { flex: 1, overflowY: 'auto', padding: 24 },

  // Profile
  profileGrid: {
    display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14,
  },
  profileItem: {
    background: '#0a0a1a', borderRadius: 12,
    border: '1px solid #1e1e3a', padding: '14px 16px',
  },
  profileLabel: { color: '#666', fontSize: 11, letterSpacing: 0.5, marginBottom: 6 },
  profileValue: { color: '#fff', fontSize: 15, fontWeight: '600' },

  // Wallet
  addBalanceBox: {
    background: '#0a0a1a', borderRadius: 12,
    border: '1px solid #f4c43033', padding: 16, marginBottom: 20,
  },
  addBalanceTitle: { color: '#f4c430', fontSize: 14, fontWeight: 'bold', marginBottom: 12 },
  input: {
    background: '#111128', border: '1px solid #333', borderRadius: 8,
    color: '#fff', fontSize: 14, padding: '10px 12px', outline: 'none',
  },
  addBtn: {
    background: '#f4c430', border: 'none', borderRadius: 8,
    color: '#000', fontSize: 13, fontWeight: 'bold',
    padding: '10px 18px', cursor: 'pointer', whiteSpace: 'nowrap',
  },
  previewRow: {
    display: 'flex', justifyContent: 'space-between',
    background: '#f4c43011', borderRadius: 8, padding: '8px 12px',
    marginTop: 4,
  },
  historyTitle: { color: '#888', fontSize: 12, fontWeight: '600', letterSpacing: 1, marginBottom: 12 },
  historyRow: {
    display: 'flex', alignItems: 'center', gap: 14,
    padding: '12px 0', borderBottom: '1px solid #1e1e3a',
  },
  emptySmall: { textAlign: 'center', color: '#555', padding: 30 },

  // Slots
  slotRow: {
    display: 'flex', justifyContent: 'space-between', alignItems: 'center',
    padding: '12px 0', borderBottom: '1px solid #1e1e3a',
  },
  slotLeft: { flex: 1, marginRight: 16 },
};